import { throttle } from "lodash-es";
import { type ForwardedRef, type RefObject, useEffect, useState } from "react";

import { useForwardedRef } from "./useForwardedRef.ts";

interface Position {
  x: number;
  y: number;
}

interface Box extends Position {
  width: number;
  height: number;
}

// Collision detection between 2 boxes
const isCollide = (a: Box, b: Box): boolean =>
  !(a.y + a.height < b.y || a.y > b.y + b.height || a.x + a.width < b.x || a.x > b.x + b.width);

// Calculate the distance using the Pythagorean Theorem (a^2 + b^2 = c^2)
const getDistance = (a: Position, b: Position): number => {
  const distanceSquared = Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2);
  return Math.sqrt(distanceSquared);
};

// Get distance between a box and a pointer position
const getBoxDistance = (a: Box, b: Position) => {
  if (!a || !b) return;

  const collide = isCollide(a, { x: b.x, y: b.y, width: 1, height: 1 });

  // Distances from the corners
  const topLeft = getDistance({ x: a.x, y: a.y }, b);
  const topright = getDistance({ x: a.x + a.width, y: a.y }, b);
  const bottomRight = getDistance({ x: a.x + a.width, y: a.y + a.height }, b);
  const bottomLeft = getDistance({ x: a.x, y: a.y + a.height }, b);

  // Distances from the borders
  const alignedHoriz = b.y >= a.y && b.y <= a.y + a.height;
  const alignedVert = b.x >= a.x && b.x <= a.x + a.width;
  const top = Math.abs(a.y - b.y);
  const right = Math.abs(a.x + a.width - b.x);
  const bottom = Math.abs(a.y + a.height - b.y);
  const left = Math.abs(a.x - b.x);

  return Math.round(
    collide
      ? 0
      : alignedHoriz
        ? Math.min(right, left)
        : alignedVert
          ? Math.min(top, bottom)
          : Math.min(topLeft, topright, bottomRight, bottomLeft),
  );
};

const useDistance = (ref: RefObject<Element | null> | ForwardedRef<Element>) => {
  const [distance, setDistance] = useState<number>();
  const elRef = useForwardedRef(ref);

  useEffect(() => {
    if (!elRef?.current) return;

    const listener = throttle((event: PointerEvent) => {
      // We get it on every events because the box may have moves between events
      const elBox: Box | undefined = elRef?.current?.getBoundingClientRect();

      if (elBox) setDistance(getBoxDistance(elBox, event));
    }, 85); // every 5 frames

    window.addEventListener("pointermove", listener);
    return () => {
      window.removeEventListener("pointermove", listener);
    };
  }, [elRef]);

  return distance ?? null;
};

export { useDistance };
